import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { bookingAPI } from '../services/api';
import CustomAlert from '../components/CustomAlert';
import completeTick from '../assets/images/DashboardImages/complete_tick.svg';
import assignDriverIcon from '../assets/images/Driverimages/AssignDriver.svg';
import assignDriverWhiteIcon from '../assets/images/Formicons/driver-assign-white.svg';
import startServiceIcon from '../assets/images/Driverimages/completed-jobs.svg';
import markArrivedIcon from '../assets/images/Driverimages/active-driver.svg';
import markCompleteIcon from '../assets/images/Driverimages/MarkComplete.svg';
import "../css/bookingDetails.css";
import { ShimmerBox } from "../components/Shimmer";
import "../css/Shimmer.css";
import bookingsEmptyIcon from "../assets/images/EmptyIcons/Bookings-Not-Found.svg";
import customersEmptyIcon from "../assets/images/EmptyIcons/empty-customers.svg";

const BookingDetails = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("customer");
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showChangeDriverModal, setShowChangeDriverModal] = useState(false);
  const [showServiceCompleteModal, setShowServiceCompleteModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [changingDriver, setChangingDriver] = useState(false);

  // Booking data state
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Available drivers state
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [assigningDriver, setAssigningDriver] = useState(false);

  // Custom Alert State
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    type: "success",
    title: "",
    message: "",
  });

  // Custom Alert Helper
  const showCustomAlert = (type, title, message) => {
    setAlertConfig({ type, title, message });
    setShowAlert(true);
  };

  // Fetch booking details
  const fetchBookingDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await bookingAPI.getBookingDetails(bookingId);
      const data = response.data;

      if (data.Status === "Success") {
        setBooking(data.Booking);
      } else {
        throw new Error(data.Message || "Failed to fetch booking details");
      }
    } catch (err) {
      setError(
        err.response?.data?.Message ||
          err.message ||
          "Failed to fetch booking details"
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch available drivers
  const fetchAvailableDrivers = async (search = "") => {
    setDriversLoading(true);

    try {
      const response = await bookingAPI.getAvailableDrivers(bookingId, search);
      const data = response.data;

      if (data.Status === "Success") {
        setAvailableDrivers(data.Drivers || []);
      }
    } catch (err) {
      // Error fetching drivers
    } finally {
      setDriversLoading(false);
    }
  };

  // Assign driver
  const handleAssignDriver = async () => {
    if (!selectedDriver) return;

    setAssigningDriver(true);

    try {
      const response = await bookingAPI.assignDriver({
        DriverId: selectedDriver.DriverId,
        BookingId: parseInt(bookingId),
      });

      const data = response.data;

      if (data.Status === "Success") {
        // Close modal and refresh booking details
        setShowAssignModal(false);
        setSelectedDriver(null);
        setSearchQuery("");
        fetchBookingDetails();
        showCustomAlert("success", "Success", "Driver assigned successfully!");
      } else {
        throw new Error(data.Message || "Failed to assign driver");
      }
    } catch (err) {
      showCustomAlert(
        "error",
        "Error",
        "Failed to assign driver: " +
          (err.response?.data?.Message || err.message)
      );
    } finally {
      setAssigningDriver(false);
    }
  };

  // Change driver
  const handleChangeDriver = async () => {
    if (!selectedDriver) return;

    setChangingDriver(true);

    try {
      const response = await bookingAPI.editBookingDriver({
        BookingId: parseInt(bookingId),
        DriverId: selectedDriver.DriverId,
      });

      const data = response.data;

      if (data.Status === "Success") {
        // Close modal and refresh booking details
        setShowChangeDriverModal(false);
        setSelectedDriver(null);
        setSearchQuery("");
        fetchBookingDetails();
        showCustomAlert("success", "Success", data.Message || "Driver changed successfully!");
      } else {
        throw new Error(data.Message || "Failed to change driver");
      }
    } catch (err) {
      showCustomAlert(
        "error",
        "Error",
        "Failed to change driver: " +
          (err.response?.data?.Message || err.message)
      );
    } finally {
      setChangingDriver(false);
    }
  };

  // Fetch booking on mount
  useEffect(() => {
    if (bookingId) {
      fetchBookingDetails();
    }
  }, [bookingId]);

  // Fetch drivers when modal opens
  useEffect(() => {
    if (showAssignModal || showChangeDriverModal) {
      fetchAvailableDrivers();
    }
  }, [showAssignModal, showChangeDriverModal]);

  // Search drivers with debounce
  useEffect(() => {
    if (showAssignModal || showChangeDriverModal) {
      const timer = setTimeout(() => {
        fetchAvailableDrivers(searchQuery);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchQuery, showAssignModal, showChangeDriverModal]);

  // Format address
  const formatAddress = (addr) => {
    if (!addr) return "N/A";
    const parts = [
      addr.AddressLine1,
      addr.AddressLine2,
      addr.City,
      addr.State,
      addr.ZipCode,
      addr.Country,
    ].filter(Boolean);
    return parts.join(", ") || "N/A";
  };

  // Get action button icon
  const getActionIcon = (key, isCompleted) => {
    if (key === 'AssignDriver' && !isCompleted) {
      return assignDriverWhiteIcon;
    }
    const icons = {
      AssignDriver: assignDriverIcon,
      MarkArrived: markArrivedIcon,
      StartService: startServiceIcon,
      MarkComplete: markCompleteIcon,
    };
    return icons[key] || assignDriverIcon;
  };

  // Handle action button click
  const handleActionClick = (action) => {
    if (action.Key === 'MarkComplete' && action.IsCompleted) {
      setShowServiceCompleteModal(true);
      return;
    }

    if (!action.IsEnabled || action.IsCompleted) return;

    if (action.Key === "AssignDriver") {
      setShowAssignModal(true);
    }
    // Other actions (MarkArrived, StartService) - API not available yet
  };

  // Loading state - Shimmer
  if (loading) {
    return (
      <div className="booking-details-page">
        <div className="content-wrapper">
          {/* Left Section Shimmer */}
          <div className="left-section">
            {/* Tabs Shimmer */}
            <div className="tabs-container booking_details_tab">
              <ShimmerBox width="120px" height="40px" borderRadius="8px" />
              <ShimmerBox width="120px" height="40px" borderRadius="8px" />
              <ShimmerBox width="120px" height="40px" borderRadius="8px" />
            </div>
            {/* Details Box Shimmer */}
            <div className="details-box">
              {[1, 2, 3, 4, 5].map((item) => (
                <div key={item} className="details-row">
                  <ShimmerBox width="120px" height="14px" />
                  <ShimmerBox width="180px" height="14px" />
                </div>
              ))}
            </div>
          </div>
          {/* Right Section Shimmer */}
          <div className="right-section">
            <div className="actions-card">
              <div className="actions-header">
                <ShimmerBox width="80px" height="18px" />
                <ShimmerBox width="70px" height="24px" borderRadius="4px" />
              </div>
              <div className="actions-buttons-wrapper">
                {[1, 2, 3, 4].map((item) => (
                  <div
                    key={item}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "16px",
                      background: "#f8f9fa",
                      borderRadius: "8px",
                      marginBottom: "12px",
                    }}
                  >
                    <ShimmerBox width="40px" height="40px" borderRadius="8px" />
                    <div style={{ flex: 1 }}>
                      <ShimmerBox width="100px" height="14px" />
                      <ShimmerBox
                        width="140px"
                        height="12px"
                        style={{ marginTop: "6px" }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        {/* Bottom Grid Shimmer */}
        <div className="bottom-grid">
          <div className="card order-activity-card">
            <div className="card-header">
              <ShimmerBox width="120px" height="18px" />
            </div>
            <div className="card-body">
              {[1, 2, 3, 4].map((item) => (
                <div
                  key={item}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "16px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <ShimmerBox width="10px" height="10px" borderRadius="50%" />
                    <ShimmerBox width="150px" height="14px" />
                  </div>
                  <ShimmerBox width="80px" height="14px" />
                </div>
              ))}
            </div>
          </div>
          <div className="address-column">
            <div className="card address-card">
              <div className="card-header">
                <ShimmerBox width="130px" height="18px" />
              </div>
              <div className="card-body">
                <ShimmerBox width="100px" height="16px" />
                <ShimmerBox
                  width="100%"
                  height="14px"
                  style={{ marginTop: "8px" }}
                />
                <ShimmerBox
                  width="80%"
                  height="14px"
                  style={{ marginTop: "6px" }}
                />
              </div>
            </div>
            <div className="card address-card">
              <div className="card-header">
                <ShimmerBox width="120px" height="18px" />
              </div>
              <div className="card-body">
                <ShimmerBox width="100px" height="16px" />
                <ShimmerBox
                  width="100%"
                  height="14px"
                  style={{ marginTop: "8px" }}
                />
                <ShimmerBox
                  width="80%"
                  height="14px"
                  style={{ marginTop: "6px" }}
                />
              </div>
            </div>
          </div>
          <div className="card payment-card">
            <div className="card-header">
              <ShimmerBox width="130px" height="18px" />
            </div>
            <div className="card-body">
              {[1, 2, 3, 4, 5].map((item) => (
                <div key={item} className="pay-row">
                  <ShimmerBox width="100px" height="14px" />
                  <ShimmerBox width="120px" height="14px" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="booking-details-page">
        <div className="state-container error">
          <div className="state-icon">⚠️</div>
          <h3 className="state-title">Something Went Wrong</h3>
          <p className="state-text">{error}</p>
          <button className="state-btn" onClick={() => navigate("/bookings")}>
            Back to Bookings
          </button>
        </div>
      </div>
    );
  }

  // No booking found
  if (!booking) {
    return (
      <div className="booking-details-page">
        <div className="state-container empty">
          <img
            src={bookingsEmptyIcon}
            alt="Booking Not Found"
            className="empty-state-icon"
          />
          <h3 className="state-title">Booking Not Found</h3>
          <p className="state-text">
            The booking you're looking for doesn't exist or has been removed.
          </p>
          <button className="state-btn" onClick={() => navigate("/bookings")}>
            Back to Bookings
          </button>
        </div>
      </div>
    );
  }

  const {
    CustomerDetails,
    ServiceDetails,
    DriverDetails,
    Actions,
    OrderActivity,
    ShippingAddress,
    BillingAddress,
    PaymentDetails,
  } = booking;

  return (
    <div className="booking-details-page">
      {/* Main Content Wrapper */}
      <div className="content-wrapper">
        {/* Left Section: Tabs + Details */}
        <div className="left-section">
          {/* Tabs */}
          <div className="tabs-container booking_details_tab">
            <button
              className={`tab-btn ${activeTab === "customer" ? "active" : ""}`}
              onClick={() => setActiveTab("customer")}
            >
              Customer Details
            </button>
            <button
              className={`tab-btn ${activeTab === "service" ? "active" : ""}`}
              onClick={() => setActiveTab("service")}
            >
              Service Details
            </button>
            <button
              className={`tab-btn ${activeTab === "driver" ? "active" : ""}`}
              onClick={() => setActiveTab("driver")}
            >
              Driver Details
            </button>
          </div>

          {/* Details Box - Tab Content */}
          <div className="details-box">
            {/* Customer Details Tab */}
            {activeTab === "customer" && (
              <>
                <div className="details-row">
                  <p>Customer Name</p>
                  <span>{CustomerDetails?.Name || "N/A"}</span>
                </div>
                <div className="details-row">
                  <p>Customer Phone</p>
                  <span>{CustomerDetails?.Phone || "N/A"}</span>
                </div>
                <div className="details-row">
                  <p>Customer Email Id</p>
                  <span>{CustomerDetails?.Email || "N/A"}</span>
                </div>
                <div className="details-row">
                  <p>Scheduled Date & Time</p>
                  <span>{CustomerDetails?.ScheduledDateTime || "N/A"}</span>
                </div>
                <div className="details-row">
                  <p>Delivery Location</p>
                  <span>{CustomerDetails?.DeliveryLocation || "N/A"}</span>
                </div>
              </>
            )}

            {/* Service Details Tab */}
            {activeTab === "service" && (
              <>
                <div className="details-row">
                  <p>Service Details</p>
                  <span>
                    {ServiceDetails?.Services?.[0]?.ServiceName || "N/A"}
                  </span>
                </div>
                <div className="details-row">
                  <p>Service</p>
                  <span>
                    {ServiceDetails?.Services?.[0]?.SubService || "N/A"}
                  </span>
                </div>
                <div className="details-row">
                  <p>Quantity</p>
                  <span>
                    {ServiceDetails?.Services?.[0]?.Quantity}{" "}
                    {ServiceDetails?.Services?.[0]?.Unit || ""}
                  </span>
                </div>
                <div className="details-row">
                  <p>Base Name</p>
                  <span>
                    {ServiceDetails?.BaseName
                      ? ServiceDetails.BaseName
                      : "None"}
                  </span>
                </div>
                <div className="details-row">
                  <p>Total Price</p>
                  <span className="price-highlight">
                    ${ServiceDetails?.TotalPrice || 0}
                  </span>
                </div>
              </>
            )}

            {/* Driver Details Tab */}
            {/* Driver Details Tab */}
            {activeTab === "driver" && (
              <>
                {/* Check if booking is cancelled first */}
                {booking.Status === "Cancelled" ||
                booking.Status === "CANCELLED" ? (
                  <div className="no-driver-state">
                    <img
                      src={customersEmptyIcon}
                      alt="Order Cancelled"
                      className="empty-state-icon"
                    />
                    <h3 className="no-driver-title">Order Cancelled</h3>
                    <p className="no-driver-text">
                      {DriverDetails?.IsAssigned
                        ? `Driver ${
                            DriverDetails?.DriverName || ""
                          } was assigned before cancellation`
                        : "No driver was assigned to this order"}
                    </p>
                  </div>
                ) : DriverDetails?.IsAssigned ? (
                  <>
                    <div className="details-row">
                      <p>Driver Name</p>
                      <span>{DriverDetails?.DriverName || "N/A"}</span>
                    </div>
                    <div className="details-row">
                      <p>Phone No</p>
                      <span>{DriverDetails?.DriverPhone || "N/A"}</span>
                    </div>
                    <div className="details-row">
                      <p>Driver Rating</p>
                      <span className="rating">
                        {DriverDetails?.DriverRating || "N/A"} ⭐
                      </span>
                    </div>
                    <div className="details-row">
                      <p>Vehicle Type</p>
                      <span>{DriverDetails?.VehicleType || "N/A"}</span>
                    </div>
                    <div className="details-row">
                      <p>Vehicle Number</p>
                      <span>{DriverDetails?.VehicleNumber || "N/A"}</span>
                    </div>
                  </>
                ) : (
                  <div className="no-driver-state">
                    <img
                      src={customersEmptyIcon}
                      alt="No Driver"
                      className="empty-state-icon"
                    />
                    <h3 className="no-driver-title">No Driver Assigned</h3>
                    <p className="no-driver-text">
                      Assign a driver to start this service
                    </p>
                    <button
                      className="assign-btn"
                      onClick={() => setShowAssignModal(true)}
                    >
                      Assign Driver
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right Section: Actions Card */}
        <div className="right-section">
          <div className="actions-card">
            <div className="actions-header">
              <span>Actions</span>
              <label className="status-tag">{booking.Status}</label>
            </div>

            <div className="actions-buttons-wrapper">
              {Actions?.map((action, index) => {
                const isCompleted = action.IsCompleted;
                const isDisabled = !action.IsEnabled;
                const isCancelled =
                  booking.Status === "Cancelled" ||
                  booking.Status === "CANCELLED";

                // Only apply 'assign' class when it's actually clickable
                const isAssignActive = action.Key === 'AssignDriver' && !isCompleted && !isCancelled && action.IsEnabled;

                // For AssignDriver when completed OR driver is assigned, show driver name and change button
                const isDriverAssigned = action.Key === 'AssignDriver' && (isCompleted || DriverDetails?.IsAssigned);
                if (isDriverAssigned) {
                  // Check if MarkArrived is completed - if yes, don't show Change button
                  const markArrivedAction = Actions?.find(a => a.Key === 'MarkArrived');
                  const isMarkArrivedCompleted = markArrivedAction?.IsCompleted;
                  // Show Change button only if: MarkArrived not completed AND booking not cancelled
                  const showChangeButton = !isMarkArrivedCompleted && !isCancelled;

                  return (
                    <div key={action.Key} className="action-btn completed assigndriver">
                      <img src={getActionIcon(action.Key, true)} alt="" className="action-icon" />
                      <div className="btn-text-simple">
                        <span className={`btn-title ${action.Key}`}>{action.Label}</span>
                        <span className="btn-subtitle driver-assigned-name">{DriverDetails?.DriverName || 'Driver Assigned'}</span>
                      </div>
                      {showChangeButton ? (
                        <button
                          className="change-driver-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowChangeDriverModal(true);
                          }}
                        >
                          Change
                        </button>
                      ) : (
                        <img
                          src={completeTick}
                          alt="Completed"
                          className="action-tick-icon"
                        />
                      )}
                    </div>
                  );
                }

                return (
                  <button
                    key={action.Key}
                    className={`action-btn ${action.Key === 'AssignDriver' && !isCompleted ? 'assign' : action.Key.toLowerCase()} ${isCompleted ? 'completed' : ''} ${isDisabled && !isCompleted ? 'disabled' : ''} ${action.Key === 'MarkComplete' && isCompleted ? 'clickable' : ''}`}
                    onClick={() => handleActionClick(action)}
                    disabled={action.Key === 'MarkComplete' && isCompleted ? false : (isDisabled || isCompleted)}
                  >
                    <img src={getActionIcon(action.Key, isCompleted)} alt="" className="action-icon" />
                    <div className="btn-text-simple">
                      <span className={`btn-title ${action.Key}`}>{action.Label}</span>
                      <span className="btn-subtitle">{action.Description}</span>
                    </div>
                    {isCompleted && (
                      <img
                        src={completeTick}
                        alt="Completed"
                        className="action-tick-icon"
                      />
                    )}

                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM GRID */}
      <div className="bottom-grid">
        {/* Order Activity Card */}
        <div className="card order-activity-card">
          <div className="card-header">
            <h3>Order Activity</h3>
          </div>
          <div className="card-body">
            <ul className="activity-list">
              {OrderActivity?.map((activity, index) => (
                <li key={index}>
                  <div className="activity-item">
                    <span
                      className={`dot ${activity.IsCompleted ? "active" : ""}`}
                    ></span>
                    <span className="activity-text">{activity.Activity}</span>
                  </div>
                  <span className="time">
                    {activity.DateTime ? (
                      <>
                        {activity.DateTime.split(", ")[0]}
                        <br />
                        {activity.DateTime.split(", ")[1]}
                      </>
                    ) : (
                      "-"
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Shipping & Billing Address Column */}
        <div className="address-column">
          {/* Shipping Address Card */}
          <div className="card address-card">
            <div className="card-header">
              <h3>Shipping Address</h3>
            </div>
            <div className="card-body">
              <div className="address-block">
                <p className="name">{ShippingAddress?.Name || "N/A"}</p>
                <p className="address-text">{formatAddress(ShippingAddress)}</p>
              </div>
            </div>
          </div>

          {/* Billing Address Card */}
          <div className="card address-card">
            <div className="card-header">
              <h3>Billing Address</h3>
            </div>
            <div className="card-body">
              <div className="address-block">
                <p className="name">{BillingAddress?.Name || "N/A"}</p>
                <p className="address-text">{formatAddress(BillingAddress)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Details Card */}
        <div className="card payment-card">
          <div className="card-header">
            <h3>Payment Details</h3>
          </div>
          <div className="card-body">
            <div className="pay-row">
              <p>Payment Method</p>
              <span>{PaymentDetails?.PaymentMethod || "N/A"}</span>
            </div>
            <div className="pay-row">
              <p>Payment Status</p>
              <span
                className={
                  PaymentDetails?.PaymentStatus === "COMPLETED"
                    ? "status-completed"
                    : ""
                }
              >
                {PaymentDetails?.PaymentStatus || "N/A"}
              </span>
            </div>
            <div className="pay-row">
              <p>Amount Paid</p>
              <span className="amount">${PaymentDetails?.AmountPaid || 0}</span>
            </div>
            <div className="pay-row">
              <p>Transaction ID</p>
              <span>{PaymentDetails?.TransactionId || "N/A"}</span>
            </div>
            <div className="pay-row">
              <p>Payment Date</p>
              <span>{PaymentDetails?.PaymentDate || "N/A"}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ASSIGN DRIVER MODAL */}
      {showAssignModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowAssignModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header bt">
              <h2>Assign Driver/Technician</h2>
              <button
                className="close-btn"
                onClick={() => setShowAssignModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {driversLoading ? (
                <div className="drivers-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading drivers...</p>
                </div>
              ) : availableDrivers.length > 0 ? (
                <>
                  <div className="search-box">
                    <input
                      type="text"
                      placeholder="Search by name"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <span className="search-icon">🔍</span>
                  </div>

                  <div className="drivers-list">
                    {availableDrivers.map((driver) => (
                      <div
                        key={driver.DriverId}
                        className={`driver-item ${
                          selectedDriver?.DriverId === driver.DriverId
                            ? "selected"
                            : ""
                        }`}
                        onClick={() => setSelectedDriver(driver)}
                      >
                        <div className="driver-avatar">
                          {driver.ProfileImage ? (
                            <img
                              src={driver.ProfileImage}
                              alt={driver.DriverName}
                            />
                          ) : (
                            <div className="avatar-placeholder">
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12Z"
                                  fill="#94A3B8"
                                />
                                <path
                                  d="M12 14C7.59 14 4 16.69 4 20V22H20V20C20 16.69 16.41 14 12 14Z"
                                  fill="#94A3B8"
                                />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="driver-info">
                          <h4>{driver.DriverName}</h4>
                          <p>
                            {driver.DriverRating
                              ? `⭐ ${driver.DriverRating}`
                              : "No rating"}{" "}
                            • {driver.TasksCompleted || 0} Jobs Completed
                          </p>
                        </div>
                        <div className="radio-btn">
                          <input
                            type="radio"
                            name="driver"
                            checked={
                              selectedDriver?.DriverId === driver.DriverId
                            }
                            onChange={() => setSelectedDriver(driver)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    className="assign-driver-btn"
                    onClick={handleAssignDriver}
                    disabled={!selectedDriver || assigningDriver}
                  >
                    {assigningDriver ? "ASSIGNING..." : "ASSIGN DRIVER"}
                  </button>
                </>
              ) : (
                <div className="no-drivers-state">
                  <div className="no-drivers-icon">
                    <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                      <circle cx="32" cy="32" r="32" fill="#F1F5F9" />
                      <path
                        d="M32 24C35.3137 24 38 21.3137 38 18C38 14.6863 35.3137 12 32 12C28.6863 12 26 14.6863 26 18C26 21.3137 28.6863 24 32 24Z"
                        stroke="#94A3B8"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M44 52V48C44 44.6863 41.3137 42 38 42H26C22.6863 42 20 44.6863 20 48V52"
                        stroke="#94A3B8"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M46 28L50 32M50 32L46 36M50 32H40"
                        stroke="#94A3B8"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <h3 className="no-drivers-title">No Drivers Available</h3>
                  <p className="no-drivers-text">
                    There are no drivers available to assign at the moment.
                    Please try again later or add new drivers.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CHANGE DRIVER MODAL */}
      {showChangeDriverModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowChangeDriverModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header bt">
              <h2>Change Driver/Technician</h2>
              <button
                className="close-btn"
                onClick={() => setShowChangeDriverModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {driversLoading ? (
                <div className="drivers-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading drivers...</p>
                </div>
              ) : availableDrivers.length > 0 ? (
                <>
                  <div className="search-box">
                    <input
                      type="text"
                      placeholder="Search by name"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <span className="search-icon">🔍</span>
                  </div>

                  <div className="drivers-list">
                    {availableDrivers.map((driver) => (
                      <div
                        key={driver.DriverId}
                        className={`driver-item ${
                          selectedDriver?.DriverId === driver.DriverId
                            ? "selected"
                            : ""
                        }`}
                        onClick={() => setSelectedDriver(driver)}
                      >
                        <div className="driver-avatar">
                          {driver.ProfileImage ? (
                            <img
                              src={driver.ProfileImage}
                              alt={driver.DriverName}
                            />
                          ) : (
                            <div className="avatar-placeholder">
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12Z"
                                  fill="#94A3B8"
                                />
                                <path
                                  d="M12 14C7.59 14 4 16.69 4 20V22H20V20C20 16.69 16.41 14 12 14Z"
                                  fill="#94A3B8"
                                />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="driver-info">
                          <h4>{driver.DriverName}</h4>
                          <p>
                            {driver.DriverRating
                              ? `⭐ ${driver.DriverRating}`
                              : "No rating"}{" "}
                            • {driver.TasksCompleted || 0} Jobs Completed
                          </p>
                        </div>
                        <div className="radio-btn">
                          <input
                            type="radio"
                            name="change-driver"
                            checked={
                              selectedDriver?.DriverId === driver.DriverId
                            }
                            onChange={() => setSelectedDriver(driver)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    className="assign-driver-btn"
                    onClick={handleChangeDriver}
                    disabled={!selectedDriver || changingDriver}
                  >
                    {changingDriver ? "CHANGING..." : "CHANGE DRIVER"}
                  </button>
                </>
              ) : (
                <div className="no-drivers-state">
                  <div className="no-drivers-icon">
                    <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                      <circle cx="32" cy="32" r="32" fill="#F1F5F9" />
                      <path
                        d="M32 24C35.3137 24 38 21.3137 38 18C38 14.6863 35.3137 12 32 12C28.6863 12 26 14.6863 26 18C26 21.3137 28.6863 24 32 24Z"
                        stroke="#94A3B8"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M44 52V48C44 44.6863 41.3137 42 38 42H26C22.6863 42 20 44.6863 20 48V52"
                        stroke="#94A3B8"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M46 28L50 32M50 32L46 36M50 32H40"
                        stroke="#94A3B8"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <h3 className="no-drivers-title">No Drivers Available</h3>
                  <p className="no-drivers-text">
                    There are no drivers available to assign at the moment.
                    Please try again later or add new drivers.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* COMPLETED SERVICE DETAILS MODAL */}
      {showServiceCompleteModal && (() => {
        const markCompleteAction = Actions?.find(a => a.Key === 'MarkComplete');
        const completionImage = markCompleteAction?.CompletionImage;
        const completionDate = markCompleteAction?.CompletionImageUploadedAt || markCompleteAction?.CompletedAt;

        // Format date - API already returns formatted string like "22 Dec at 15:04 Hours"
        const formatCompletionDate = (dateStr) => {
          if (!dateStr) return null;
          // API returns pre-formatted string, just add "On " prefix
          return `On ${dateStr}`;
        };

        return (
          <div className="modal-overlay" onClick={() => setShowServiceCompleteModal(false)}>
            <div className="modal-content service-complete-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Completed Service Details</h2>
                <div className="modal-header-right">
                  <span className="status-badge completed">Completed</span>
                  <button className="close-btn" onClick={() => setShowServiceCompleteModal(false)}>
                    ✕
                  </button>
                </div>
              </div>

              <div className="modal-body service-complete-body">
                <div className="proof-completion-wrapper">
                  <h3 className="section-title">Proof of Completion</h3>
                  <div className="proof-image-container">
                    {completionImage ? (
                      <img src={completionImage} alt="Proof of Completion" className="proof-image" />
                    ) : (
                      <div className="proof-placeholder">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                          <rect x="3" y="3" width="18" height="18" rx="2" stroke="#94A3B8" strokeWidth="1.5"/>
                          <circle cx="8.5" cy="8.5" r="1.5" fill="#94A3B8"/>
                          <path d="M21 15L16 10L5 21" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>No image available</span>
                      </div>
                    )}
                  </div>
                  {formatCompletionDate(completionDate) ? (
                    <p className="completion-date">{formatCompletionDate(completionDate)}</p>
                  ) : completionImage ? (
                    <p className="completion-date">Completion date not available</p>
                  ) : (
                    <p className="completion-date">Service not yet completed</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Custom Alert Modal */}
      <CustomAlert
        show={showAlert}
        onClose={() => setShowAlert(false)}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
      />
    </div>
  );
};

export default BookingDetails;
